import { useState, type FormEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowLeft, Mail } from 'lucide-react';
import { api, ApiError } from '../lib/api.ts';
import { PRIVACY_URL, TERMS_URL } from '../lib/links.ts';

// Magic-link request screen. Submits email to
// POST /v1/public/invoices/:publicId/signin and surfaces the
// "check your email" confirmation. Backend rate-limits per email + per IP.
function SigninPage() {
  const { publicId } = Route.useParams();
  const { reason } = Route.useSearch();
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus('submitting');
    setError(null);
    try {
      await api.requestMagicLink(publicId, email);
      setStatus('sent');
    } catch (err) {
      setStatus('idle');
      if (err instanceof ApiError && err.code === 'EmailDoesNotMatchInvoice') {
        setError(intl.formatMessage({ id: 'signin.error.emailMismatch' }));
      } else {
        setError(intl.formatMessage({ id: 'signin.error.generic' }));
      }
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-10">
      <Link
        to="/r/$publicId"
        params={{ publicId }}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-navy-600)] hover:text-[var(--color-navy-900)]"
      >
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        <FormattedMessage id="tracking.cta.back" />
      </Link>

      <div className="mt-4 rounded-xl border border-[var(--color-navy-100)] bg-white p-6 shadow-sm">
        {status === 'sent' ? (
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--color-navy-900)]">
              <Mail size={20} strokeWidth={1.5} aria-hidden="true" />
              <FormattedMessage id="signin.success.heading" />
            </h1>
            <p className="mt-2 text-sm text-[var(--color-navy-600)]">
              <FormattedMessage id="signin.success.body" />
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {reason === 'expired' && (
              <p
                role="alert"
                className="mb-4 rounded-md border border-[var(--color-amber-200,#fde68a)] bg-[var(--color-amber-50,#fffbeb)] px-3 py-2 text-sm text-[var(--color-amber-800,#92400e)]"
              >
                <FormattedMessage id="signin.session.expired" />
              </p>
            )}
            <h1 className="text-xl font-semibold text-[var(--color-navy-900)]">
              <FormattedMessage id="signin.heading" />
            </h1>
            <p className="mt-1 text-sm text-[var(--color-navy-600)]">
              <FormattedMessage id="signin.subheading" />
            </p>

            <label className="mt-6 block text-sm font-medium text-[var(--color-navy-800)]">
              <FormattedMessage id="signin.email.label" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={intl.formatMessage({ id: 'signin.email.placeholder' })}
                className="mt-1 block w-full rounded-md border border-[var(--color-navy-200)] bg-white px-3 py-2 text-sm text-[var(--color-navy-900)] focus:outline-none focus:ring-2 focus:ring-[var(--color-navy-500)]"
              />
            </label>

            {error && (
              <p className="mt-3 text-sm text-[var(--color-danger)]" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting' || email.length === 0}
              className="mt-6 w-full rounded-md bg-[var(--color-navy-800)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-navy-700)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FormattedMessage id="signin.submit" />
            </button>

            <p className="mt-3 text-xs text-[var(--color-navy-500)]">
              <FormattedMessage
                id="signin.tos"
                values={{
                  terms: (chunks) => (
                    <a
                      href={TERMS_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-[var(--color-navy-700)]"
                    >
                      {chunks}
                    </a>
                  ),
                  privacy: (chunks) => (
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-[var(--color-navy-700)]"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// Inline (anonymous) return type rather than a named interface: the route is
// re-exported through the generated routeTree.gen.ts, and this app's typecheck
// is a composite build (`tsc -b`) that emits declarations. A named, non-exported
// type here trips TS4023 ("cannot be named") in the generated file; a structural
// literal serializes cleanly.
export const Route = createFileRoute('/r/$publicId/signin')({
  validateSearch: (search: Record<string, unknown>): { reason?: 'expired' } =>
    search['reason'] === 'expired' ? { reason: 'expired' } : {},
  component: SigninPage,
});
